/**
 * NETWORK LAYER
 * =============
 * This is the ONE place in the app that actually talks to the backend over the
 * network. Every screen calls through here instead of using `fetch` directly,
 * so all requests share the same behaviour:
 *
 *   • prepend the API base URL              (config.ts)
 *   • attach the user's auth headers        (lib/cookies.ts)
 *   • send JSON and parse JSON back
 *   • turn any non-2xx response into a clear, typed error (ApiError)
 *
 * If the auth scheme or error format ever changes, this is the only file to
 * edit — the rest of the app doesn't notice.
 */
import { API_BASE_URL, API_LOCALE_LANGUAGE, API_TENANT } from '../config'
import { getIdentityHeaders } from '../lib/cookies'

/** A network/HTTP failure with a status code, so callers can react to it. */
export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST'
  /** JSON body for POST requests (objects are stringified automatically). */
  body?: unknown
  /** Lets a caller cancel the request (used by the useApi hook on unmount). */
  signal?: AbortSignal
}

/**
 * Core request function. Generic `<T>` is the shape you expect back, e.g.
 *   const feed = await request<HoroscopeFeedResponse>('/horoscope/feed')
 */
/** Total request budget — a hung backend must never leave the screen stuck on the loader. */
const REQUEST_TIMEOUT_MS = 30000

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options

  // Allow callers to pass either a path ('/horoscope/feed') or a full URL.
  const isAbsolute = /^https?:\/\//.test(path)
  const url = isAbsolute ? path : `${API_BASE_URL}${path}`

  // Abort on either a timeout or the caller's signal (useApi aborts on unmount),
  // merged into one controller. clearTimeout/removeEventListener in `finally`
  // so we never leak the timer or listener.
  const controller = new AbortController()
  const onCallerAbort = () => controller.abort()
  if (signal) {
    if (signal.aborted) controller.abort()
    else signal.addEventListener('abort', onCallerAbort, { once: true })
  }
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  // Only attach identity (auth headers) to our own API — i.e. relative paths
  // resolved against API_BASE_URL. An absolute URL could be arbitrary (e.g. ever
  // sourced from server-driven data), and we must not leak the user's identity
  // headers to another host.
  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers: {
        // Content-Type only when there's a JSON body — on a bodyless GET it's
        // meaningless and (not being CORS-safelisted) it forces a preflight.
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        'X-TENANT': API_TENANT,
        'LOCALE-LANGUAGE': API_LOCALE_LANGUAGE,
        // V2/accessToken identity — the gateway validates these and injects the
        // authorized-userid itself. Only for our own API (relative path), never
        // an arbitrary absolute URL.
        ...(isAbsolute ? {} : { 'AUTH-VERSION': 'V2', ...getIdentityHeaders() }),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      // Identity rides in the X-SHARECHAT-* headers, not cookies. The gateway is
      // cross-origin, so 'include' would (a) send nothing useful (page-origin
      // cookies aren't sent cross-origin) and (b) force the server to echo an
      // exact origin + Access-Control-Allow-Credentials (a '*' allow-list would
      // break the WebView). 'omit' keeps the CORS contract simple.
      credentials: 'omit',
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timeoutId)
    if (signal) signal.removeEventListener('abort', onCallerAbort)
  }

  if (!response.ok) {
    // Try to read an error payload, but don't blow up if there isn't one.
    let errorBody: unknown
    try {
      errorBody = await response.json()
    } catch {
      /* response had no JSON body */
    }
    throw new ApiError(response.status, response.statusText || 'Request failed', errorBody)
  }

  // Some endpoints legitimately return an empty body — handle that gracefully.
  const text = await response.text()
  if (!text) return null as T
  try {
    return JSON.parse(text) as T
  } catch {
    // A 2xx with a non-JSON body (e.g. an HTML error/redirect/login page from an
    // intermediary) — surface it as ApiError so callers see one typed shape, not
    // a raw SyntaxError that escapes the ApiError contract.
    throw new ApiError(response.status, 'Expected JSON but got a non-JSON response', text)
  }
}

/** The public API surface the rest of the app uses. */
export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
}
